import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from '@app/App';
import { AppProviders } from '@app/providers';
import { registerPreloadErrorAutoReload } from '@core/preloadErrorReload';
import '@shared/styles/tailwind.css';

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Missing #root in index.html');

// After a deploy, a stale tab fails to lazy-load a (now-replaced) route chunk. Auto-reload once so it
// picks up the fresh bundle instead of white-screening. Registered before render so it's always armed.
registerPreloadErrorAutoReload();

// Hotjar is NO LONGER loaded here. It is consent-gated: `PrivacyLayer` (mounted in `App`) loads it only
// for out-of-scope users or once an in-scope (EU/EEA) user grants analytics consent — see src/features/privacy.

// `useTransitions={false}`: react-router 7 wraps every location update in `React.startTransition` by
// default (the v6 `v7_startTransition` flag, now on). In a transition, React keeps the OLD page on screen
// while a lazy route chunk loads instead of showing that route's `<Suspense>` fallback, so a first click on
// a not-yet-loaded page would look like nothing happened until the chunk arrived. Off keeps the v6
// behaviour every route here was built for: the "Loading…" fallback at once (docs/architecture/overview.md
// §3.3). It is also react-router's stated opt-out for apps that use `useSyncExternalStore`, which the
// live-quote and position stores do. Pinned by src/main.test.tsx.
//
// StrictMode lives INSIDE AppProviders, not outside. react-oidc-context's `AuthProvider` runs
// the Cognito code-exchange in a mount effect; under StrictMode that effect fires twice in dev,
// and the second call tries to spend an already-consumed authorization code → silent failure →
// the /auth/callback page hangs on "Completing sign-in…" forever. AuthProvider must mount once.
// All non-OIDC children still get StrictMode's double-render bug detection.
createRoot(rootElement).render(
  <BrowserRouter useTransitions={false}>
    <AppProviders>
      <StrictMode>
        <App />
      </StrictMode>
    </AppProviders>
  </BrowserRouter>,
);
